import React, { useState, useEffect } from 'react';

/**
 * Accessibility Toolbar Component — themed to match GoGameDev3D dark aesthetic
 * תפריט נגישות מקיף בהתאם לתקן הישראלי 5568 ו-WCAG 2.0 Level AA
 */
const AccessibilityToolbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    contrast: 'normal',
    readableFont: false,
    highlightLinks: false,
    highlightTitles: false,
    cursor: 'normal',
    hideImages: false,
    stopAnimations: false,
    lineHeight: 'normal',
    letterSpacing: 'normal',
    textAlign: 'right',
    screenReader: false,
  });

  useEffect(() => {
    const savedSettings = localStorage.getItem('accessibilitySettings');
    if (savedSettings) {
      try { setSettings(JSON.parse(savedSettings)); }
      catch (e) { console.error('Failed to load accessibility settings'); }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
    const root = document.documentElement;
    root.style.fontSize = `${settings.fontSize}%`;
    root.classList.remove('high-contrast', 'dark-contrast', 'yellow-black', 'inverted-colors');
    if (settings.contrast !== 'normal') root.classList.add(settings.contrast);
    root.style.fontFamily = settings.readableFont ? 'Arial, Helvetica, sans-serif' : '';
    root.classList.toggle('highlight-links', settings.highlightLinks);
    root.classList.toggle('highlight-titles', settings.highlightTitles);
    root.classList.remove('large-cursor', 'black-large-cursor');
    if (settings.cursor === 'large') root.classList.add('large-cursor');
    else if (settings.cursor === 'black-large') root.classList.add('black-large-cursor');
    root.classList.toggle('hide-images', settings.hideImages);
    root.classList.toggle('stop-animations', settings.stopAnimations);
    root.style.lineHeight = settings.lineHeight === 'large' ? '2' : '';
    root.style.letterSpacing = settings.letterSpacing === 'large' ? '0.12em' : '';
    root.style.textAlign = settings.textAlign;
  }, [settings]);

  const resetSettings = () => setSettings({
    fontSize: 100, contrast: 'normal', readableFont: false,
    highlightLinks: false, highlightTitles: false, cursor: 'normal',
    hideImages: false, stopAnimations: false, lineHeight: 'normal',
    letterSpacing: 'normal', textAlign: 'right', screenReader: false,
  });

  const increaseFontSize = () => setSettings(p => ({ ...p, fontSize: Math.min(p.fontSize + 10, 200) }));
  const decreaseFontSize = () => setSettings(p => ({ ...p, fontSize: Math.max(p.fontSize - 10, 80) }));
  const toggleSetting = (key) => setSettings(p => ({ ...p, [key]: !p[key] }));
  const changeSetting = (key, value) => setSettings(p => ({ ...p, [key]: value }));

  return (
    <>
      <style>{`
        :root {
          --a11y-accent: #5CA7DD;
          --a11y-accent-strong: #2F79AD;
          --a11y-accent-rgb: 92, 167, 221;
          --a11y-secondary: #34D9B5;
          --a11y-panel: rgba(7, 11, 22, 0.985);
          --a11y-header: rgba(14, 21, 38, 0.98);
          --a11y-surface: #131C33;
          --a11y-surface-hover: #1A2947;
          --a11y-text: #EDF2FB;
          --a11y-muted: #A3B1CA;
          --a11y-faint: #8FA0BC;
          --a11y-line: rgba(125, 156, 215, 0.22);
          --a11y-line-soft: rgba(125, 156, 215, 0.11);
          --a11y-shadow: rgba(0, 0, 0, 0.62);
          --a11y-overlay: rgba(3, 7, 16, 0.64);
          --a11y-tab: rgba(19, 28, 51, 0.96);
          --a11y-tab-hover: #1A2947;
          --a11y-tab-icon: #9DC3E6;
        }
        :root[data-theme="light"] {
          --a11y-accent: #2F79AD;
          --a11y-accent-strong: #1D5C8F;
          --a11y-accent-rgb: 47, 121, 173;
          --a11y-secondary: #238B7E;
          --a11y-panel: rgba(244, 250, 255, 0.985);
          --a11y-header: rgba(232, 243, 251, 0.98);
          --a11y-surface: #FAFDFF;
          --a11y-surface-hover: #E2F0F9;
          --a11y-text: #0E1A2B;
          --a11y-muted: #46566F;
          --a11y-faint: #5A6B84;
          --a11y-line: rgba(29, 92, 143, 0.25);
          --a11y-line-soft: rgba(29, 92, 143, 0.13);
          --a11y-shadow: rgba(26, 59, 92, 0.28);
          --a11y-overlay: rgba(18, 42, 62, 0.34);
          --a11y-tab: rgba(226, 240, 249, 0.97);
          --a11y-tab-hover: #D5EAF6;
          --a11y-tab-icon: #2F6FA3;
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }

        /* Toggle button */
        .a11y-toggle-btn {
          position: fixed;
          top: 12.5%;
          right: 0;
          transform: translateY(-50%);
          background: var(--a11y-tab);
          color: var(--a11y-tab-icon);
          border: none;
          border-radius: 10px 0 0 10px;
          padding: 6px 6px;
          cursor: pointer;
          z-index: 9998;
          border: 1px solid var(--a11y-line);
          border-right: 0;
          box-shadow: -2px 0 12px var(--a11y-shadow);
          transition: all 0.3s ease;
          font-size: 0.4rem;
          width: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 4px;
        }
        .a11y-toggle-btn:hover, .a11y-toggle-btn:focus {
          background: var(--a11y-tab-hover);
          transform: translateY(-50%) translateX(-4px);
          outline: none;
          border-color: var(--a11y-accent);
          box-shadow: -3px 0 16px var(--a11y-shadow);
        }
        .a11y-toggle-btn:focus-visible {
          outline: 3px solid var(--a11y-text);
          outline-offset: 2px;
        }
        .a11y-toggle-btn.open {
          background: var(--a11y-tab-hover);
          border-color: var(--a11y-accent);
        }
        .a11y-toggle-label {
          font-size: 0.42rem;
          font-weight: 700;
          letter-spacing: 0.02em;
          writing-mode: vertical-rl;
          text-orientation: mixed;
          color: var(--a11y-muted);
          white-space: nowrap;
        }

        /* Panel */
        .a11y-panel {
          position: fixed;
          top: 0; right: 0;
          width: min(320px, 90vw);
          height: 100vh;
          background: var(--a11y-panel);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border-left: 1px solid var(--a11y-line);
          box-shadow: -8px 0 40px var(--a11y-shadow);
          z-index: 9999;
          overflow-y: auto;
          overflow-x: hidden;
          direction: rtl;
          padding: 0 0 100px;
          color: var(--a11y-text);
          animation: slideInRight 0.3s cubic-bezier(0.4,0,0.2,1);
          scrollbar-width: thin;
          scrollbar-color: rgba(var(--a11y-accent-rgb),0.42) transparent;
        }
        .a11y-panel::-webkit-scrollbar { width: 4px; }
        .a11y-panel::-webkit-scrollbar-track { background: transparent; }
        .a11y-panel::-webkit-scrollbar-thumb { background: rgba(var(--a11y-accent-rgb),0.5); border-radius: 4px; }

        .a11y-overlay {
          position: fixed;
          inset: 0;
          background: var(--a11y-overlay);
          backdrop-filter: blur(3px);
          -webkit-backdrop-filter: blur(3px);
          z-index: 9997;
        }

        /* Panel header */
        .a11y-header {
          position: sticky;
          top: 0;
          background: var(--a11y-header);
          backdrop-filter: blur(20px);
          padding: 20px 20px 16px;
          border-bottom: 1px solid var(--a11y-line-soft);
          z-index: 10;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .a11y-header-stripe {
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 2px;
          background: linear-gradient(90deg, var(--a11y-accent-strong), var(--a11y-secondary));
          border-radius: 0;
        }
        .a11y-title {
          font-size: 1.1rem;
          font-weight: 800;
          margin: 0;
          color: var(--a11y-text);
        }
        .a11y-close-btn {
          background: var(--a11y-surface);
          border: 1px solid var(--a11y-line);
          color: var(--a11y-muted);
          width: 34px; height: 34px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }
        .a11y-close-btn:hover, .a11y-close-btn:focus {
          background: rgba(239,68,68,0.2);
          border-color: rgba(239,68,68,0.4);
          color: #fca5a5;
          outline: none;
        }
        .a11y-close-btn:focus-visible {
          outline: 2px solid var(--a11y-accent);
          outline-offset: 2px;
        }

        /* Section */
        .a11y-section {
          padding: 16px 20px;
          border-bottom: 1px solid var(--a11y-line-soft);
        }
        .a11y-section-title {
          font-size: 0.78rem;
          font-weight: 700;
          color: var(--a11y-faint);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin: 0 0 12px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .a11y-section-title::before {
          content: '';
          display: inline-block;
          width: 3px; height: 14px;
          background: linear-gradient(to bottom, var(--a11y-accent), var(--a11y-secondary));
          border-radius: 2px;
          flex-shrink: 0;
        }

        /* Font size controls */
        .a11y-font-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .a11y-font-btn {
          flex: 1;
          background: var(--a11y-surface);
          border: 1px solid var(--a11y-line);
          color: var(--a11y-text);
          padding: 10px;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 700;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .a11y-font-btn:hover:not(:disabled), .a11y-font-btn:focus:not(:disabled) {
          background: var(--a11y-surface-hover);
          border-color: var(--a11y-accent);
          outline: none;
        }
        .a11y-font-btn:disabled { opacity: 0.35; cursor: default; }
        .a11y-font-btn:focus-visible { outline: 2px solid var(--a11y-accent); outline-offset: 2px; }

        .a11y-font-display {
          text-align: center;
          font-size: 0.78rem;
          color: var(--a11y-muted);
          margin-top: 8px;
          padding: 6px;
          background: var(--a11y-surface);
          border: 1px solid var(--a11y-line-soft);
          border-radius: 8px;
          font-weight: 600;
        }

        /* Toggle buttons */
        .a11y-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 11px 14px;
          margin-bottom: 8px;
          border-radius: 10px;
          border: 1px solid var(--a11y-line-soft);
          background: var(--a11y-surface);
          color: var(--a11y-muted);
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 600;
          transition: all 0.2s ease;
          text-align: right;
          direction: rtl;
          font-family: inherit;
        }
        .a11y-btn:hover, .a11y-btn:focus {
          background: var(--a11y-surface-hover);
          border-color: var(--a11y-line);
          color: var(--a11y-text);
          outline: none;
        }
        .a11y-btn.active {
          background: rgba(var(--a11y-accent-rgb),0.16);
          border-color: var(--a11y-accent);
          color: var(--a11y-accent);
        }
        .a11y-btn.active .a11y-indicator {
          background: var(--a11y-accent);
          box-shadow: 0 0 8px rgba(var(--a11y-accent-rgb),0.52);
        }
        .a11y-btn:focus-visible { outline: 2px solid var(--a11y-accent); outline-offset: 2px; }

        .a11y-indicator {
          width: 10px; height: 10px;
          border-radius: 50%;
          border: 2px solid rgba(var(--a11y-accent-rgb),0.58);
          background: transparent;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        /* Contrast grid */
        .a11y-contrast-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }
        .a11y-contrast-btn {
          padding: 10px 8px;
          border-radius: 10px;
          border: 1px solid var(--a11y-line-soft);
          background: var(--a11y-surface);
          color: var(--a11y-muted);
          cursor: pointer;
          font-size: 0.8rem;
          font-weight: 600;
          transition: all 0.2s ease;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          font-family: inherit;
        }
        .a11y-contrast-btn:hover { background: var(--a11y-surface-hover); border-color: var(--a11y-line); outline: none; }
        .a11y-contrast-btn.active { background: rgba(var(--a11y-accent-rgb),0.16); border-color: var(--a11y-accent); color: var(--a11y-accent); }
        .a11y-contrast-btn:focus-visible { outline: 2px solid var(--a11y-accent); outline-offset: 2px; }

        /* Reset + statement */
        .a11y-reset-btn {
          width: 100%;
          padding: 12px;
          border-radius: 10px;
          border: 1px solid rgba(239,68,68,0.25);
          background: rgba(239,68,68,0.07);
          color: #fca5a5;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 700;
          transition: all 0.2s ease;
          margin-bottom: 10px;
          font-family: inherit;
        }
        .a11y-reset-btn:hover {
          background: rgba(239,68,68,0.15);
          border-color: rgba(239,68,68,0.45);
        }
        .a11y-reset-btn:focus-visible { outline: 2px solid #f87171; outline-offset: 2px; }

        .a11y-statement-link {
          display: block;
          width: 100%;
          padding: 11px;
          border-radius: 10px;
          border: 1px solid var(--a11y-line);
          background: var(--a11y-surface);
          color: var(--a11y-accent);
          text-decoration: none;
          font-size: 0.88rem;
          font-weight: 600;
          text-align: center;
          transition: all 0.2s ease;
          box-sizing: border-box;
        }
        .a11y-statement-link:hover {
          background: var(--a11y-surface-hover);
          border-color: var(--a11y-accent);
        }
        .a11y-statement-link:focus-visible { outline: 2px solid var(--a11y-accent); outline-offset: 2px; }

        /* Global contrast & cursor modes */
        html.high-contrast { filter: contrast(150%) !important; }
        html.dark-contrast * { background-color: #000 !important; color: #fff !important; border-color: #fff !important; }
        html.yellow-black * { background-color: #000 !important; color: #ffff00 !important; border-color: #ffff00 !important; }
        html.inverted-colors { filter: invert(100%) !important; }
        html.highlight-links a { outline: 2px solid #fbbf24 !important; background: rgba(251,191,36,0.1) !important; border-radius: 2px !important; }
        html.highlight-titles h1, html.highlight-titles h2, html.highlight-titles h3,
        html.highlight-titles h4, html.highlight-titles h5, html.highlight-titles h6 {
          outline: 2px solid #60a5fa !important; background: rgba(96,165,250,0.1) !important; border-radius: 2px !important;
        }
        html.hide-images img, html.hide-images svg { visibility: hidden !important; }
        html.stop-animations *, html.stop-animations *::before, html.stop-animations *::after {
          animation: none !important; transition: none !important;
        }
        html.large-cursor, html.large-cursor * { cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Ccircle cx='24' cy='24' r='22' fill='rgba(99,102,241,0.5)' stroke='white' stroke-width='2'/%3E%3C/svg%3E") 24 24, auto !important; }
        html.black-large-cursor, html.black-large-cursor * { cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Ccircle cx='24' cy='24' r='22' fill='black' stroke='white' stroke-width='2'/%3E%3C/svg%3E") 24 24, auto !important; }
        .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border-width:0; }
      `}</style>

      {/* Toggle button */}
      <button
        className={`a11y-toggle-btn${isOpen ? ' open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'סגור תפריט נגישות' : 'פתח תפריט נגישות'}
        aria-expanded={isOpen}
        type="button"
      >
        <span aria-hidden="true" style={{ fontSize: '1.3rem' }}>♿</span>
      </button>

      {/* Panel overlay */}
      {isOpen && (
        <div
          className="a11y-overlay"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      {isOpen && (
        <div
          className="a11y-panel"
          role="dialog"
          aria-label="הגדרות נגישות"
          aria-modal="true"
        >
          {/* Header */}
          <div className="a11y-header">
            <div className="a11y-header-stripe" />
            <h2 className="a11y-title">♿ הגדרות נגישות</h2>
            <button
              className="a11y-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="סגור תפריט נגישות"
              type="button"
            >✕</button>
          </div>

          {/* Font size */}
          <div className="a11y-section">
            <h3 className="a11y-section-title">גודל טקסט</h3>
            <div className="a11y-font-controls">
              <button className="a11y-font-btn" onClick={decreaseFontSize} disabled={settings.fontSize <= 80} aria-label="הקטן טקסט">A−</button>
              <button className="a11y-font-btn" onClick={() => changeSetting('fontSize', 100)} aria-label="איפוס גודל">A</button>
              <button className="a11y-font-btn" onClick={increaseFontSize} disabled={settings.fontSize >= 200} aria-label="הגדל טקסט">A+</button>
            </div>
            <div className="a11y-font-display" role="status" aria-live="polite">גודל נוכחי: {settings.fontSize}%</div>
          </div>

          {/* Contrast */}
          <div className="a11y-section">
            <h3 className="a11y-section-title">ניגודיות וצבעים</h3>
            <div className="a11y-contrast-grid">
              {[
                ['high-contrast', '⚫⚪', 'ניגודיות גבוהה'],
                ['dark-contrast', '🌙', 'ניגודיות כהה'],
                ['yellow-black', '🟡', 'צהוב על שחור'],
                ['inverted-colors', '🔄', 'הפוך צבעים'],
              ].map(([val, icon, label]) => (
                <button
                  key={val}
                  className={`a11y-contrast-btn${settings.contrast === val ? ' active' : ''}`}
                  onClick={() => changeSetting('contrast', settings.contrast === val ? 'normal' : val)}
                  aria-pressed={settings.contrast === val}
                >
                  <span style={{ fontSize: '1.2rem' }}>{icon}</span>
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Text */}
          <div className="a11y-section">
            <h3 className="a11y-section-title">הגדרות טקסט</h3>
            {[
              ['readableFont', 'גופן קריא (Arial)', 'toggle'],
              ['lineHeight-large', 'מרווח בין שורות', 'lineHeight'],
              ['letterSpacing-large', 'מרווח בין אותיות', 'letterSpacing'],
            ].map(([key, label, type]) => {
              const isActive = type === 'toggle' ? settings[key]
                : type === 'lineHeight' ? settings.lineHeight === 'large'
                : settings.letterSpacing === 'large';
              const handleClick = () => {
                if (type === 'toggle') toggleSetting(key);
                else if (type === 'lineHeight') changeSetting('lineHeight', settings.lineHeight === 'large' ? 'normal' : 'large');
                else changeSetting('letterSpacing', settings.letterSpacing === 'large' ? 'normal' : 'large');
              };
              return (
                <button key={key} className={`a11y-btn${isActive ? ' active' : ''}`} onClick={handleClick} aria-pressed={isActive}>
                  <span>{label}</span>
                  <div className="a11y-indicator" />
                </button>
              );
            })}
          </div>

          {/* Highlight */}
          <div className="a11y-section">
            <h3 className="a11y-section-title">הדגשות</h3>
            {[
              ['highlightLinks', 'הדגש קישורים 🔗'],
              ['highlightTitles', 'הדגש כותרות 📌'],
            ].map(([key, label]) => (
              <button key={key} className={`a11y-btn${settings[key] ? ' active' : ''}`} onClick={() => toggleSetting(key)} aria-pressed={settings[key]}>
                <span>{label}</span>
                <div className="a11y-indicator" />
              </button>
            ))}
          </div>

          {/* Cursor */}
          <div className="a11y-section">
            <h3 className="a11y-section-title">סמן עכבר</h3>
            {[
              ['large', 'סמן גדול ⚪'],
              ['black-large', 'סמן גדול שחור ⚫'],
            ].map(([val, label]) => (
              <button key={val} className={`a11y-btn${settings.cursor === val ? ' active' : ''}`} onClick={() => changeSetting('cursor', settings.cursor === val ? 'normal' : val)} aria-pressed={settings.cursor === val}>
                <span>{label}</span>
                <div className="a11y-indicator" />
              </button>
            ))}
          </div>

          {/* Display */}
          <div className="a11y-section">
            <h3 className="a11y-section-title">תצוגה</h3>
            {[
              ['hideImages', 'הסתר תמונות 🚫'],
              ['stopAnimations', 'עצור אנימציות ⏸️'],
            ].map(([key, label]) => (
              <button key={key} className={`a11y-btn${settings[key] ? ' active' : ''}`} onClick={() => toggleSetting(key)} aria-pressed={settings[key]}>
                <span>{label}</span>
                <div className="a11y-indicator" />
              </button>
            ))}
          </div>

          {/* Reset + link */}
          <div className="a11y-section">
            <button className="a11y-reset-btn" onClick={resetSettings} aria-label="אפס את כל הגדרות הנגישות">
              🔄 אפס את כל ההגדרות
            </button>
            <a href="/accessibility" className="a11y-statement-link" aria-label="עבור להצהרת נגישות">
              📋 הצהרת נגישות מלאה
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityToolbar;
